import { Stack } from "@mui/system";
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import {
  setDefinitionModalSourceForContradictingST,
  setIsDefinitionModalOpenForContradictingST,
  setSelectedSearchTermForContradictingSTDefinition,
} from "../../../../../../../../../features/searchTermTabSlice";
import { selectUserData } from "../../../../../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useSearchParams } from "react-router-dom";
import ContradictingSTFilterItems from "./ContradictingSTFilterItems";
import RemoveNegativeDefinitionModal from "./RemoveNegativeDefinitionModal";

function ContradictingSTFilterCard() {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasCreateSearchTermTabPermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "plan_campaign_ppc",
    "create_st_control",
  );

  const {
    contradictingSTDefinition,
    selectedSearchTermForContradictingSTDefinition,
    selectedRowsForContradictingSTDefinition,
    isDefinitionModalOpenForContradictingST,
    confirmLoaderForContradictingSTDefinition,
  } = useSelector((state: any) => state.searchTermTab);

  //-- Function to handle close of Negative Definition Modal
  const handleCloseDefinitionModal = () => {
    dispatch(setIsDefinitionModalOpenForContradictingST(false)); // Close the modal
    dispatch(setDefinitionModalSourceForContradictingST("")); // Clear source (headerIcon or saveButton)
  };

  const clearSelectedSearchTerm = () => {
    dispatch(setSelectedSearchTermForContradictingSTDefinition(""));
  };

  //Autocomplete search options
  const searchOptions = useMemo(() => {
    const set = new Set<string>();
    (contradictingSTDefinition || []).forEach((r: any) => {
      if (typeof r?.search_term === "string") set.add(r.search_term.trim());
    });
    return Array.from(set); // ["shoes", "hats", ...]s
  }, [contradictingSTDefinition]);

  const handleClickRemoveNegativeDefinition = () => {
    dispatch(setDefinitionModalSourceForContradictingST("headerIcon"));
    dispatch(setIsDefinitionModalOpenForContradictingST(true));
  };

  const hasSelection = selectedRowsForContradictingSTDefinition.length > 0;

  //-- this hold search params in url
  useEffect(() => {
    // Update selectedTemplate when searchParams change (e.g., on back/forward navigation)
    dispatch(
      setSelectedSearchTermForContradictingSTDefinition(
        searchParams.get("search"),
      ),
    );
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ padding: "8px 16px", marginTop: "8px", boxShadow: "none" }}>
        <Stack
          flexDirection={"row"}
          justifyContent={"start"}
          gap={2}
          // sx={{ padding: "16px 16px 0px 16px" }}
        >
          <Autocomplete
            fullWidth
            disablePortal
            freeSolo //user can type anything
            data-tracker="555"
            sx={{
              width: "390px",
              // ↓ Always show clear button when searchbox has value
              "& .MuiAutocomplete-clearIndicator": {
                visibility: selectedSearchTermForContradictingSTDefinition
                  ? "visible"
                  : "hidden",
              },
            }}
            options={searchOptions}
            value={selectedSearchTermForContradictingSTDefinition || ""} // string or null
            disableClearable={!selectedSearchTermForContradictingSTDefinition} //✕ only when there is a value
            onInputChange={(_, newVal) => {
              //Every keystroke → Redux → shows X button
              dispatch(
                setSelectedSearchTermForContradictingSTDefinition(newVal ?? ""),
              );
            }}
            onChange={(_, newValue: string | null) => {
              dispatch(
                setSelectedSearchTermForContradictingSTDefinition(
                  newValue ?? "",
                ),
              );
              setSearchParams((prevParams) => {
                const updatedParams = Object.fromEntries(prevParams); // Preserve previous params
                if (newValue) {
                  updatedParams.search = newValue; // Set new value
                  updatedParams.page = "0";
                } else {
                  delete updatedParams.search; // Remove from URL if cleared
                }
                return updatedParams;
              });
            }}
            getOptionLabel={(opt) => opt || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Term"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ marginLeft: "12px" }} />
                    </InputAdornment>
                  ),
                }}
                color="secondary"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();

                    const inputValue = (
                      e.target as HTMLInputElement
                    ).value.trim();
                    if (!inputValue) return;

                    // dispatch
                    dispatch(
                      setSelectedSearchTermForContradictingSTDefinition(
                        inputValue,
                      ),
                    );

                    // update URL params
                    setSearchParams((prevParams) => {
                      const updatedParams = Object.fromEntries(prevParams);
                      updatedParams.search = inputValue;
                      updatedParams.page = "0";
                      return updatedParams;
                    });

                    // close dropdown
                    (e.target as HTMLInputElement).blur();
                  }
                }}
              />
            )}
            size="small"
          />
          <Stack
            flexDirection={"row"}
            justifyContent={"space-between"}
            sx={{ width: "100%" }}
          >
            <ContradictingSTFilterItems
              clearSelectedSearchTerm={clearSelectedSearchTerm}
            />
            {/* --- Bulk Actions Header --- */}
            <Stack gap={0.5}>
              {/* Row for Set As icons */}
              <Stack
                flexDirection="row"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {hasCreateSearchTermTabPermission ? (
                    <Button
                      data-tracker="558"
                      variant="outlined"
                      color="secondary"
                      onClick={handleClickRemoveNegativeDefinition}
                      disabled={
                        !hasSelection ||
                        confirmLoaderForContradictingSTDefinition
                      }
                    >
                      Remove Negative Definition
                    </Button>
                  ) : (
                    <Button variant="outlined" color="secondary" disabled>
                      Remove Negative Definition
                    </Button>
                  )}
                </Stack>
              </Stack>

              {/* Message BELOW */}
              <Typography variant="body2" color="text.secondary">
                * To perform bulk actions, select at least one row.
              </Typography>
            </Stack>
          </Stack>

          {/* Dialog for Remove Negative Definition */}
          <RemoveNegativeDefinitionModal
            open={isDefinitionModalOpenForContradictingST}
            onClose={handleCloseDefinitionModal}
          />
        </Stack>
      </Paper>
    </>
  );
}
export default ContradictingSTFilterCard;
